import * as moment from 'moment-timezone';
import createModelInstance from 'utils/createModelInstance';
import Pet from 'models/Pet';
import Owner from 'models/Owner';
import isEmpty from 'lodash/isEmpty';
import { getImage } from 'services/booking';

const Booking = (bookingObj = {}) => {
    // Get the user timezone from the browser
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

    /**
     * Parses the raw order stage to simple values
     *
     * @param {Object} bookingObj
     * @returns {string}
     */
    const parseOrderStage = (bookingObj) => {
        if (bookingObj.cstm.order_stage_c !== 'Cancelled' && bookingObj.cstm.is_final_order_stage_c) {
            return 'Completed';
        }

        // If booking is_open_order_c (Saved and Converted) set to true.
        // We should not proceed to this logic
        if (bookingObj.cstm.is_open_order_c !== 1) {
            if (bookingObj.cstm.order_stage_c === 'Pet Collected - Pending Details') {
                return 'Pet Collected - Pending Details';
            }

            if (bookingObj.cstm.order_stage_c === 'VP Unsubmitted') {
                return 'Unsubmitted';
            }

            if (bookingObj.cstm.order_stage_c === 'VP Submitted') {
                return 'Submitted';
            }
        }

        return 'In Progress';
    };
    /**
     * Get VP submitted date
     *
     * @param {Object} bookingObj
     * @returns {string}
     */
     const getVPSubmittedDate = (bookingObj) => {
        let order_dates
        if (bookingObj.order_events) {
            order_dates = bookingObj.order_events.find(v => v.to_value === 'VP Submitted')
            if(order_dates) {
                return order_dates.date_modified
            } else {
                return null;
            }
            
        }
    };

    /**
     * This will set the client details from client info
     * @param {*} clientDetail
     * @param {*} origClientDetail
     * @returns
     */
     const setClientDetailsFromClient = (clientDetail, origClientDetail) => {
        return {
            first_name: bookingObj && bookingObj.cstm && bookingObj.cstm.provided_client_details_c ? '' : origClientDetail.first_name,
            last_name: bookingObj && bookingObj.cstm && bookingObj.cstm.provided_client_details_c ? '' : origClientDetail.last_name,
            phone_mobile: bookingObj && bookingObj.cstm && bookingObj.cstm.provided_client_details_c ? '' : clientDetail[0].phone_alternate,
            primary_address_city: bookingObj && bookingObj.cstm && bookingObj.cstm.provided_client_details_c ? '' : clientDetail[0].billing_address_city,
            primary_address_country: bookingObj && bookingObj.cstm && bookingObj.cstm.provided_client_details_c ? '' : clientDetail[0].billing_address_country,
            primary_address_postalcode: bookingObj && bookingObj.cstm && bookingObj.cstm.provided_client_details_c ? '' : clientDetail[0].billing_address_postalcode,
            primary_address_state: bookingObj && bookingObj.cstm && bookingObj.cstm.provided_client_details_c ? '' : clientDetail[0].billing_address_state,
            primary_address_street: bookingObj && bookingObj.cstm && bookingObj.cstm.provided_client_details_c ? '' : clientDetail[0].billing_address_street,
            email_address: bookingObj && bookingObj.cstm && bookingObj.cstm.provided_client_details_c ? '' : origClientDetail.email_address,
        }
    }

    // Creates an empty booking object when there's no input data
    // Assign the data from an object coming from the Sugar API
    let newBooking = {
        id: '',
        orderStage: bookingObj && bookingObj.cstm ? bookingObj.cstm.order_stage_c : '',
        parsedOrderStage: bookingObj && bookingObj.cstm ? parseOrderStage(bookingObj) : '',
        dateEntered: '',
        specialInstructions: [],
        quotedProducts: [],
        product: null, // Used for bundle booking products
        serviceType: bookingObj && bookingObj.cstm && bookingObj.cstm.service_type
            ? bookingObj.cstm.service_type
            : '',
        salesStage: '',
        salesStatus: '',
        opportunityType: '',
        isPackage: bookingObj && bookingObj.cstm ? bookingObj.cstm.is_package_c : false,
        skipOwnerDetails: bookingObj && bookingObj.cstm ? bookingObj.cstm.provided_client_details_c : false,
        orderId: bookingObj && bookingObj.cstm ? bookingObj.cstm.order_id_c : '',
        orderSubmitted: getVPSubmittedDate(bookingObj) ? moment(getVPSubmittedDate(bookingObj)): null,
        vetStaff: bookingObj && bookingObj.cstm ? bookingObj.cstm.vet_staff_c : '',
        oktp: bookingObj && bookingObj.cstm ? !!(bookingObj.cstm.ok_prepare_c) : false,
        /**
         * @todo waiting for Sugar API to add date field
         */
        oktpDate: bookingObj && bookingObj.cstm && bookingObj.cstm.ok_prepare_time_c
            ? moment(bookingObj.cstm.ok_prepare_time_c) : '',
        collection_fee: bookingObj && bookingObj.cstm && bookingObj.cstm.collection_freight_fee_c ?
            parseFloat(bookingObj.cstm.collection_freight_fee_c) : bookingObj.collection_fee ?? 0.00,
        return_fee: bookingObj && bookingObj.cstm && bookingObj.cstm.return_freight_fee_c ?
            parseFloat(bookingObj.cstm.return_freight_fee_c) : bookingObj.return_fee ?? 0.00,
    };
    // Creates the instance based on the key/obj by default
    newBooking = createModelInstance(newBooking, bookingObj);

    // Creates date objects from the date fields
    newBooking.dateEntered = newBooking.dateEntered ? moment(newBooking.dateEntered) : '';

    const inputPetData = bookingObj.pet ? bookingObj.pet : {};
    let inputOwnerData = bookingObj.owner ? bookingObj.owner : {};
    // Formats the data from Sugar API
    if (bookingObj && Object.hasOwnProperty.call(bookingObj, 'cstm') && bookingObj.cstm) {
        // Creates the Pet Object based on SugarAPI data format
        for (const key in bookingObj.cstm) {
            if (key.includes('pet_')) {
                let newKey = key.replace('pet_', '');
                newKey = newKey.replace('_c', '');
                // Only breed_type field is named different in the booking
                if (newKey === 'breed_type') {
                    newKey = 'type';
                }
                inputPetData[newKey] = bookingObj.cstm[key];
            }
        }
        // Creates the Owner Object based on SugarAPI data format
        if (bookingObj.cstm.client_details_c) {
            inputOwnerData = bookingObj.cstm.client_details_c;

            if (!isEmpty(bookingObj.client)) {
                let clientDetail = bookingObj.client;
                inputOwnerData = {...inputOwnerData, ...setClientDetailsFromClient(clientDetail, bookingObj.cstm.client_details_c)}
            }
        }
        // Adds the return to field
        if (bookingObj.cstm.return_add_option_c) {
            inputOwnerData.returnTo = bookingObj.cstm.return_add_option_c;
        }
    }
    // Assigns the Pet data to the booking instance
    newBooking.pet = Pet(inputPetData);
    // Assigns the Owner data to the booking instance
    newBooking.owner = Owner(inputOwnerData);

    return {
        ...newBooking,
        customTableFormat(extraFields) {
            return {
                orderId: this.orderId,
                type: typeof this.serviceType === 'object' ? this.serviceType.name : '',
                dateEntered: this.getDateEnteredFormat(),
                ownerName: this.owner.getFullName(),
                petName: this.pet.getFullName(),
                orderStage: this.parsedOrderStage,
                ...extraFields
            };
        },
        getDateEnteredFormat() {
            if (this.dateEntered) {
                // Convert the date entered to the user timezone
                return this.dateEntered.tz(timeZone).format('HH:mm D-MMM-YYYY');
            }
            return '';
        },
        getDateEnteredShortFormat() {
            if (this.dateEntered) {
                return this.dateEntered.format('D-MMM-YYYY');
            }
            return '';
        },
        getDateEnteredLongFormat() {
            if (this.dateEntered) {
                return this.dateEntered.format('hh:mma, ddd D MMM YYYY');
            }
            return '';
        },
        getOktpDateFormat() {
            if (this.oktpDate) {
                return this.oktpDate.tz(timeZone).format('hh:mma, ddd D MMM YYYY');
            }
            return '';
        },
        getPetDetails() {
            if (this.pet) {
                return this.pet.toCustomTable();
            }
            return [];
        },
        getOwnerDetails() {
            if (this.owner) {
                return this.owner.toCustomTable();
            }
            return [];
        },
        getServiceTypeName() {
            return typeof this.serviceType === 'object' ? this.serviceType.name : '';
        },
        getQuotedProducts() { 
            
            return this.quotedProducts.filter((item) => { 
                if(item.parentProductId === item.producttemplateIdC) {
                    item.canBeDeleted = 1;
                } else {
                    item.canBeDeleted = 0;
                }
                return true;
              }).sort((a, b) => a.seq - b.seq);
        },
        getProductsTableFormat(arProducts = {}) {
            // If there is a products array provided, use it instead of the getQuotedProducts method
            if (!isEmpty(arProducts)) {
                return arProducts.map((product) => {
                    return {
                        amount: product.quantity ? parseInt(product.quantity, 10) : product.quantity,
                        name: product.name ? product.name : product.name,
                        unitPrice: product.unitPrice || product.unitPrice !== undefined ? product.unitPrice : parseFloat(product.discountPrice),
                        engravingText: product.engravingText ? product.engravingText : null,
                        childProducts: product.childProductsCartSugarcrmFormat ? product.childProductsCartSugarcrmFormat : [],
                    };
                });
            }

            return this.getQuotedProducts().map((product) => {
                return {
                    amount: product.quantity ? parseInt(product.quantity, 10) : product.quantity,
                    name: product.name ? product.name : product.name,
                    unitPrice: product.unitPrice ? product.unitPrice : parseFloat(product.discountPrice),
                    engravingText: product.engravingText ? product.engravingText : null,
                    childProducts: product.childProductsCartSugarcrmFormat ? product.childProductsCartSugarcrmFormat : [],
                };
            });
        },
        /**
         * Formats the products in the booking to the submit format for Sugar API
         * @returns {Array} returns an array object list of the products
         */
        getProductsSubmitFormat() {
            if (!this.quotedProducts || this.quotedProducts.length <= 0) {
                return [];
            }
            return this.quotedProducts
                .filter((product) => product.actualProductIdC)
                .map((product) => {
                    const productObj = {
                        quantity: parseInt(product.quantity, 10),
                        deleted: product.deleted,
                        product: product.actualProductIdC,
                        package_products_c: !isEmpty(product.packageProductsC) ? product.packageProductsC : null,
                        engraving_text: product.engravingText,
                        editId: product.editId,
                        is_vpcreated: product.isVpcreated
                    };
                    // If has id then product is either updated or deleted
                    if (product.id) {
                        productObj.id = product.id;
                    }

                    // If the product is being deleted, remove the quantity
                    if (product.deleted) {
                        delete productObj.quantity;
                    }

                    return productObj;
                });
        },
        /**
         * Returns the table format and data for service details
         *
         * @param {Object} vetDetails
         *
         * @returns {Object}
         */
        getServiceDetails(vetDetails) {
            const data = [];

            data.push(
                ['Booking ID:', this.orderId],
                ['Service type:', this.getServiceTypeName()],
                ['Vet name:', vetDetails ? vetDetails.name : ''],
                ['Submitted by:', this.vetStaff],
            );

            return data;
        },
        /**
         * Returns the table format and data for status
         *
         * @param {Object} vetDetails
         * @param {Object} booking
         * @returns {Object}
         */
        getStatus(vetDetails,booking) {
            const data = [];
            let status = '';
            if(booking.orderStage === 'VP Submitted') {
                if(vetDetails.verRelationship === 'vet_direct' && !booking.oktp) {
                    status = 'Waiting for final instructions'
                } else if (vetDetails.verRelationship === 'vet_direct' && booking.oktp) {
                    status = 'Final instructions received' + ' ' +'['+ booking.getOktpDateFormat() +']'
                } else if (vetDetails.verRelationship === 'referral' && !booking.oktp) {
                    status = 'Waiting for final instructions and/or payment'
                } else if (vetDetails.verRelationship === 'referral' && booking.oktp) {
                    status = 'Final instructions and/or payment received' + ' ' +'['+ booking.getOktpDateFormat() +']'
                }
                data.push(
                    ['Status:', status],
                );
            } else {
               status = null
            }
          

            return data;
        },

              /**
         * Gets the booking pet body image signed url
         * 
         * @return {Promise} 
        */
        getImage() {
            return getImage(this.id);
        },

        /**
         * Check if the booking is editable
         * 
         * @returns {Boolean}
         */
        isEditable() {
            return !bookingObj?.cstm?.ok_cremate_c;
        },

        submitFormat() {
            let serviceTypeId = '';
            if (typeof this.serviceType === 'string') {
                serviceTypeId = this.serviceType;
            } else {
                serviceTypeId = this.serviceType.id;
            }
            let ownerDetails = {};
            if (!this.skipOwnerDetails) {
                ownerDetails = {
                    first_name: this.owner.firstName ? this.owner.firstName : '',
                    last_name: this.owner.lastName ? this.owner.lastName : '',
                    email_address: this.owner.email ? this.owner.email : '',
                    primary_address_street: this.owner.address ? this.owner.address.street : '',
                    primary_address_city: this.owner.address ? this.owner.address.city : '',
                    primary_address_state: this.owner.address ? this.owner.address.state : '',
                    primary_address_postalcode: this.owner.address ? this.owner.address.postalcode : '',
                    primary_address_country: this.owner.address ? this.owner.address.country : '',
                    primary_address_lat: this.owner.address.lat ? this.owner.address.lat : '0.00',
                    primary_address_lng: this.owner.address.lng ? this.owner.address.lng : '0.00',
                    return_add_option_c: this.owner.returnTo
                };
                /**
                 * Removes the initial 0 and selects the phone number type (phone or home)
                 * If number starts with 04 is phone number, otherwise is work number
                 */
                ownerDetails[this.owner.getPhoneType()] = this.owner.getFullPhoneNumber();
            }
            // If package has bundle booking products
            const product = this.product ? { product: this.product } : {};

            let collectionOption = '';
            let returnOption = '';

            if (bookingObj.vet_details) {
                switch(bookingObj.vet_details.cstm.default_collection_location_c) {
                    case 'Customer':
                        collectionOption = this.skipOwnerDetails ? '' : 'customer_address';
                    break;
                    case 'Vet':
                        collectionOption = 'vet_address';
                    break;
                    default:
                        collectionOption = bookingObj.vet_details.cstm.default_collection_location_c;
                    break;
                }

                switch(bookingObj.vet_details.cstm.default_return_location_c) {
                    case 'Customer':
                        returnOption = this.skipOwnerDetails ? '' : 'customer_address';
                    break;
                    case 'Vet':
                        returnOption = 'vet_address';
                    break;
                    default:
                        returnOption = bookingObj.vet_details.cstm.default_return_location_c;
                    break;
                }
            }


            return {
                skip_owner_details_form_c: this.pet.breed ? !!(this.skipOwnerDetails) : true,
                provided_client_details_c: this.pet.breed ? !!(this.skipOwnerDetails) : true,
                is_package_c: !!(this.isPackage),
                ...ownerDetails,
                pet_first_name_c: this.pet.firstName ? this.pet.firstName : '',
                pet_last_name_c: this.pet.lastName ? this.pet.lastName : '',
                pet_breed_type_c: this.pet.type ? this.pet.type : '',
                pet_breed_c: this.pet.breed ? this.pet.breed : '',
                pet_sex_c: this.pet.sex ? this.pet.sex : '',
                pet_weight_c: this.pet.weight ? this.pet.weight.toFixed(2) : '0.00',
                custom_unit_value_c: this.pet.customUnit ? this.pet.customUnit : null,
                class_pet_service_types_id_c: serviceTypeId,
                office_instruction_c: '',
                collection_add_option_c: collectionOption,
                return_add_option_c: returnOption,
                line_items: this.getProductsSubmitFormat(),
                ...product,
                vet_staff_c: this.vetStaff,
                createBookingFlag: localStorage.getItem('createBookingFlag'),
                has_default_product: bookingObj.has_default_product,
            };
        },
        submitVetSupply() {
            return {
                line_items: this.getProductsSubmitFormat()
            };
        }
    };
};

export default Booking;
